import React, { useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../../../../api/config";
import { useSentMessageMutation } from "../../../../api/supportMessagesAPI";
import { useCurrentUserProfile } from "../../../../utils/hooks/useCurrentUserProfile";
import Button, { ButtonModeE } from "../../../../components/Button/Button";
import Form from "../../../../components/Form/Form";
import { ValidatorsE } from "../../../../components/Form/yupValidations";
import { H1, H3, P } from "../../../../components/Text/Text";
import { ContactsPageTypeE, UserRoleE } from "../../../../models/enums";
import { FormFieldT } from "../../../../models/models";
import { useAppSelector } from "../../../../store/hooks";
import { selectCurrentUser } from "../../../../store/selectors/authSelectors";
import { TOAST_DURATION } from "../../../../utils/helpers/constants";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import { reqCheck } from "../../../../assets/images";

import styles from "./supportForm.module.scss";

const SupportForm = () => {
  const { t } = useTranslate();
  const [sentMessage, { isLoading }] = useSentMessageMutation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const user = useAppSelector(selectCurrentUser);
  // spec-42 — identity-scoped read: pre-filling this form from `useGetUserByIdQuery(...).data` sent
  // the PREVIOUS account's company/name/email/phone out of the app in a support message after an
  // account switch. `userData` is now the current user's row or nothing.
  const { profile: userData } = useCurrentUserProfile();
  const formFields: FormFieldT[] = [
    {
      label: "Company name",
      validation: ValidatorsE.REQUIRED_STRING,
      // Optional chaining is REQUIRED, not tidiness: this array is built during render, before the
      // `userData &&` guard on the <Form/> below, so a legitimately absent profile (guest, mid
      // account switch, failed UserGet) threw here — TypeError, blank page.
      defaultValue:
        user?.role === UserRoleE.DeliveryService ? undefined : userData?.ClientName,
    },
    {
      label: "Full name",
      validation: ValidatorsE.REQUIRED_STRING,
      defaultValue:
        user?.role === UserRoleE.DeliveryService
          ? undefined
          : userData
          ? `${userData.FirstName} ${userData.LastName}`
          : undefined,
    },
    {
      label: "Email",
      validation: ValidatorsE.EMAIL,
      defaultValue:
        user?.role === UserRoleE.DeliveryService ? undefined : userData?.Email,
    },
    {
      label: "Phone number",
      validation: ValidatorsE.PHONE_NUMBER,
      defaultValue:
        user?.role === UserRoleE.DeliveryService
          ? undefined
          : userData?.PhoneNumber,
    },
    {
      label: "Message",
      type: "textarea",
      validation: ValidatorsE.REQUIRED_STRING,
    },
  ];

  const handleSubmitForm = async (data: any) => {
    const body = {
      Company: data.companyName,
      CompanyIndustry: "Support",
      Country: "IL",
      Email: data.email,
      EmailBody: data.message,
      FullName: data.fullName,
      PhoneNumber: data.phoneNumber,
      PageType: window.location.pathname.includes("support")
        ? ContactsPageTypeE.SUPPORT
        : ContactsPageTypeE.CONTACT,
    };

    try {
      const res = await fetch(
        BASE_URL + "api/BusinessPages/LandingPageFormSubmit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      if (res.status === 200) {
        toast.success(t`Successfully sent`, {
          autoClose: TOAST_DURATION,
        });
        setIsSubmitted(true);
      } else if (res.status === 400 || res.status === 500) {
        toast.error(t`Something went wrong. Please try again.`, {
          autoClose: TOAST_DURATION,
        });
      }
    } catch (err) {
      toast(t`Error submitting form`, {
        type: "error",
        autoClose: TOAST_DURATION,
      });
      console.log(err);
    }
  };

  return (
    <div className={styles.wrapper}>
      <H1>Support</H1>
      <H3>Our team would love to hear from you!</H3>
      {isSubmitted ? (
        <div className={styles.successSection}>
          <img src={reqCheck} alt="success" />
          <P>
            Your message has been sent successfully. We will get back to you as
            soon as possible.
          </P>
          <Button
            onClick={() => setIsSubmitted(false)}
            mode={ButtonModeE.OUTLINED}
          >
            Send more
          </Button>
        </div>
      ) : (
        <>
          {userData && (
            <Form
              fields={formFields}
              onSubmit={handleSubmitForm}
              formID="support-form"
              columns={1}
            />
          )}
          <div className={styles.sendButtonWrapper}>
            <Button
              form="support-form"
              disabled={isLoading}
              loading={isLoading}
            >
              Send message
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default SupportForm;
